import { getJTI } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { useScopedQuery } from '@frontend/scope';
import { AudioScrubber, SpinningLoader, Text } from '@frontend/design-system';

export interface CachedAudioScrubberProps {
  onPlay?: () => void;
  customWidth?: number;
  filePath?: string;
  mediaId: string;
  singlePlayer?: boolean;
  tabIndex?: number;
  trackingId?: string;
  queryFn?: () => Promise<Response>;
  isCompactMode?: boolean;
  autoplay?: boolean;
  allowFetchError?: boolean;
}

export const CachedAudioScrubber = ({
  onPlay,
  customWidth,
  filePath,
  mediaId,
  singlePlayer,
  tabIndex = 0,
  trackingId = 'cached-audioScrubber',
  queryFn,
  isCompactMode,
  autoplay,
  allowFetchError = false,
}: CachedAudioScrubberProps) => {
  const { t } = useTranslation('phone');
  const token = getJTI();

  /**
   * Why fetch the resource instead of loading it directly into the audio component?
   *
   * Doing this allows us to cache the resource, so that the audio component doesn't re-fetch it every time it's rendered.
   */
  const { data, isLoading, isError } = useScopedQuery({
    queryKey: ['cached-audio', mediaId],
    queryFn: async () => {
      let response: Response;

      if (queryFn) {
        response = await queryFn();
      } else if (filePath) {
        const url = new URL(filePath);
        if (url.searchParams.has('token')) {
          url.searchParams.delete('token');
        }

        response = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        return undefined;
      }

      if (!allowFetchError && !response.ok) {
        throw new Error('Failed to fetch audio');
      }

      return response.blob().then((myBlob) => {
        const objectURL = URL.createObjectURL(myBlob);
        return objectURL;
      });
    },
    enabled: !!mediaId && (!!filePath || !!queryFn),
    staleTime: Infinity,
  });

  if (isError) {
    return <Text color='error'>{t('Error loading audio')}</Text>;
  }

  if (isLoading) {
    return <SpinningLoader size='small' />;
  }

  return data ? (
    <AudioScrubber
      key={mediaId}
      trackingId={trackingId}
      autoSize
      autoplay={autoplay}
      onPlay={onPlay}
      singlePlayer={singlePlayer}
      src={data}
      isCompactMode={isCompactMode}
      css={customWidth ? { maxWidth: `${customWidth}px` } : { maxWidth: '100%' }}
      tabIndex={tabIndex}
    />
  ) : null;
};
